// Pure math behind the Outlook day drill-down modal (`DaySummary` in
// Outlook.tsx). Kept renderer-free so the dashboard's vitest runner (no
// jsdom) can pin the rules — same deep-export pattern as incidentsSort.

/** Inputs for one day's stat row. For today, `activeAtClose` is the LIVE
 *  active count and `overdue` the live carried-over count; for a past day
 *  they come from the end-of-day rollup (null = pre-upgrade row). */
export interface DayCountsInput {
  opened: number
  resolved: number
  activeAtClose: number | null
  /** Problems opened that day and still unresolved at close. */
  newUnresolved: number | null
}

export interface DayCounts {
  /** Every problem live at some point that day: resolved + still active at
   *  close (carried-over included) — the "resolved/total" denominator. The
   *  same-day opened count is NOT a valid denominator: `resolved` includes
   *  recoveries of problems carried in from earlier days, so resolved/opened
   *  can exceed 1 ("173/170"). null when the rollup predates the EOD-state
   *  columns. */
  total: number | null
  /** Problems still active at close (live count for today). */
  stillOpen: number | null
  /** Carried-over problems (opened on earlier days) still open at close:
   *  activeAtClose - newUnresolved. null when either side is unknown. */
  overdue: number | null
}

export function dayModalCounts(input: DayCountsInput): DayCounts {
  const { resolved, activeAtClose, newUnresolved } = input
  const total = activeAtClose == null ? null : resolved + activeAtClose
  const overdue =
    activeAtClose == null || newUnresolved == null
      ? null
      : Math.max(0, activeAtClose - newUnresolved)
  return { total, stillOpen: activeAtClose, overdue }
}

/** One hour of the day-trend chart, as served by /confidence/today and
 *  /confidence/day/{day}. `score` is that hour's confidence snapshot (0..1,
 *  null when none landed); `opened`/`resolved` count problem/recovery events
 *  within the hour (null = Zabbix unreachable — the chart then draws the
 *  health line alone, never fabricated zeros). */
export interface HourPoint {
  at: string
  score: number | null
  opened: number | null
  resolved: number | null
}

/** The day-trend chart's three series from the hourly points: health as a
 *  percentage line (left axis, pinned 0–100) and opened/resolved counts
 *  (shared right axis). Hours without a snapshot are simply absent from the
 *  health line; count series are null when the counts were unavailable.
 *  `maxCount` feeds the right axis head-room. */
export interface DayTrendSeries {
  health: { x: number; y: number }[]
  opened: { x: number; y: number }[] | null
  resolved: { x: number; y: number }[] | null
  maxCount: number
}

export function dayTrendSeries(hourly: HourPoint[]): DayTrendSeries {
  const x = (h: HourPoint) => Math.floor(Date.parse(h.at) / 1000)
  const health = hourly
    .filter((h) => h.score != null)
    .map((h) => ({ x: x(h), y: Math.round(h.score! * 1000) / 10 }))
  const haveCounts = hourly.some((h) => h.opened != null || h.resolved != null)
  const counts = (pick: (h: HourPoint) => number | null) =>
    hourly.map((h) => ({ x: x(h), y: pick(h) ?? 0 }))
  const opened = haveCounts ? counts((h) => h.opened) : null
  const resolved = haveCounts ? counts((h) => h.resolved) : null
  const maxCount = Math.max(
    0,
    ...(opened ?? []).map((p) => p.y),
    ...(resolved ?? []).map((p) => p.y),
  )
  return { health, opened, resolved, maxCount }
}
